import React, { useState, useEffect, useCallback } from 'react';
import geofenceService, { Geofence, GeofenceInput } from '../services/GeofenceService';
import GeofenceMap from './GeofenceMap';
import GeofenceForm from './GeofenceForm';
import '../styles/GeofencePage.css';

const GeofencePage: React.FC = () => {
  const [geofences, setGeofences] = useState<Geofence[]>([]);
  const [selectedGeofenceId, setSelectedGeofenceId] = useState<string | null>(null);
  const [createMode, setCreateMode] = useState(false);
  const [pendingBounds, setPendingBounds] = useState<{
    ne: { lat: number; lng: number };
    sw: { lat: number; lng: number };
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchGeofences = useCallback(async () => {
    setLoading(true);
    try {
      const data = await geofenceService.getAllGeofences();
      setGeofences(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load geofences');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGeofences();
  }, [fetchGeofences]);

  const handleNewGeofence = () => {
    setError(null);
    setSelectedGeofenceId(null);
    setPendingBounds(null);
    setCreateMode(true);
  };

  const handleCancelCreate = () => {
    setCreateMode(false);
    setPendingBounds(null);
  };

  const handleRectangleCreated = (
    ne: { lat: number; lng: number },
    sw: { lat: number; lng: number }
  ) => {
    setPendingBounds({ ne, sw });
  };

  const handleFormSave = async (data: GeofenceInput) => {
    try {
      await geofenceService.createGeofence(data);
      setCreateMode(false);
      setPendingBounds(null);
      await fetchGeofences();
    } catch (err: any) {
      setError(err.message || 'Failed to save geofence');
    }
  };

  const handleFormCancel = () => {
    setPendingBounds(null);
    setCreateMode(false);
  };

  const handleSelectGeofence = (id: string) => {
    setSelectedGeofenceId(id);
    setCreateMode(false);
    setPendingBounds(null);
  };

  const handleDeleteGeofence = async (id: string, name: string) => {
    const confirmed = window.confirm(`Are you sure you want to delete "${name}"?`);
    if (!confirmed) return;

    try {
      await geofenceService.deleteGeofence(id);
      if (selectedGeofenceId === id) {
        setSelectedGeofenceId(null);
      }
      await fetchGeofences();
    } catch (err: any) {
      setError(err.message || 'Failed to delete geofence');
    }
  };

  const selectedGeofence = geofences.find((g) => g.id === selectedGeofenceId) || null;

  const renderActionSummary = (label: string, action: Geofence['actions']['arrival']) => (
    <div className="geofence-details-section">
      <h4>{label}</h4>
      <p>
        Notify: {action.notify ? 'Yes' : 'No'}
        {' · '}
        Rapid refresh: {action.rapidRefresh.enabled
          ? `${action.rapidRefresh.intervalSeconds}s for ${action.rapidRefresh.durationMinutes} min`
          : 'Off'}
      </p>
    </div>
  );

  return (
    <div className="geofence-page">
      {/* Sidebar */}
      <div className="geofence-sidebar">
        <div className="geofence-sidebar-header">
          <h2>Geofences</h2>
          <button
            className="btn-primary"
            onClick={handleNewGeofence}
            disabled={createMode}
          >
            New Geofence
          </button>
        </div>

        {error && (
          <div className="geofence-error">
            <span>{error}</span>
            <button onClick={() => setError(null)} aria-label="Dismiss error">×</button>
          </div>
        )}

        {loading ? (
          <div className="geofence-loading">Loading geofences...</div>
        ) : pendingBounds ? (
          <div className="geofence-form-container">
            <GeofenceForm
              ne={pendingBounds.ne}
              sw={pendingBounds.sw}
              onSave={handleFormSave}
              onCancel={handleFormCancel}
            />
          </div>
        ) : (
          <>
            <div className="geofence-sidebar-content">
              {geofences.length === 0 ? (
                <p className="geofence-list-empty">No geofences created yet.</p>
              ) : (
                geofences.map((g) => (
                  <div
                    key={g.id}
                    className={`geofence-list-item${g.id === selectedGeofenceId ? ' selected' : ''}`}
                    onClick={() => handleSelectGeofence(g.id)}
                  >
                    <span className="geofence-list-item-name">{g.name}</span>
                    <button
                      className="btn-danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteGeofence(g.id, g.name);
                      }}
                    >
                      Delete
                    </button>
                  </div>
                ))
              )}
            </div>

            {selectedGeofence && (
              <div className="geofence-details">
                <h3>{selectedGeofence.name}</h3>
                <div className="geofence-details-section">
                  <h4>Coordinates</h4>
                  <p>
                    NE: {selectedGeofence.ne.lat.toFixed(5)}, {selectedGeofence.ne.lng.toFixed(5)}
                    <br />
                    SW: {selectedGeofence.sw.lat.toFixed(5)}, {selectedGeofence.sw.lng.toFixed(5)}
                  </p>
                </div>
                {renderActionSummary('Arrival', selectedGeofence.actions.arrival)}
                {renderActionSummary('Departure', selectedGeofence.actions.departure)}
              </div>
            )}
          </>
        )}
      </div>

      {/* Map area */}
      <div className="geofence-map-area">
        <GeofenceMap
          geofences={geofences}
          selectedGeofenceId={selectedGeofenceId}
          onSelectGeofence={handleSelectGeofence}
          createMode={createMode}
          onRectangleCreated={handleRectangleCreated}
          onCancelCreate={handleCancelCreate}
        />
      </div>
    </div>
  );
};

export default GeofencePage;
